/**
 * useMe — bootstrap da sessão (GET /app/me). app.md §5.2.
 * Ao obter os dados, popula o sessionStore. É a fonte da verdade de status/módulos/expiração.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, unwrap } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';
import { useSessionStore } from '../stores/sessionStore';
import { meContextHeaders } from '../lib/requestContext';
import type { MeResponse } from '../types/session';

async function fetchMe(signal: AbortSignal): Promise<MeResponse> {
  // /app/me pode responder {} (sem id) para status especiais — allowEmpty para inspecionarmos.
  // Headers extras do getMe legado [application.js:1992-2000]: language/timezone/country/
  // region/city/geo/ip/version (só os que existirem). fingerprint/source/email/id já vêm do
  // apiClient (contextHeaders/authHeaders).
  const result = await api.get<MeResponse>('app/me', {
    signal,
    allowEmpty: true,
    headers: meContextHeaders(),
  });
  return unwrap(result);
}

export function useMe() {
  const setFromMe = useSessionStore((s) => s.setFromMe);

  const query = useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data && query.data.id) {
      setFromMe(query.data);
    }
  }, [query.data, setFromMe]);

  return query;
}
